import { httpClient } from "../../../http-client";
import { ApiUrlBuilder } from "../../shared/constants/apiUrl";
import {
  AnnotationFilterView,
  CreationAnnotationView,
  DeletionAnnotationView,
  GetAnnotationView, UpdateAnnotationStatusView,
  UpdateAnnotationView,
  UpdateAnnotationViewWithoutLocation,
} from '../types/annotation.types';
import { AnnotationModel, AnnotationStatus, AnnotationType } from '../models/annotation.model';

const AnnotationFloorServiceImplementation = {
  getAnnotations: (orga: string, siteId: string|null, buildingId: string|null, floorId: string|null): Promise<AnnotationModel[]> =>
    httpClient
      .get(ApiUrlBuilder.getAnnotations(orga, siteId, buildingId, floorId))
      .then((response) => response.json())
      .then((response) => {
        if (Array.isArray(response)) {
          return response.map((annotationView: unknown) => {
            if (isGetAnnotationView(annotationView)) {
              return new AnnotationModel(
                annotationView.id,
                annotationView.roomId,
                annotationView.type as AnnotationType,
                annotationView.title,
                annotationView.content,
                annotationView.x,
                annotationView.y,
                annotationView.pictureBinary,
                annotationView.status as AnnotationStatus,
                annotationView.fileName?? null,
                annotationView.location?? null,
                annotationView.date?? null,
                annotationView.siteId?? null,
                annotationView.siteCode?? null,
                annotationView.addedBy?? null,
                annotationView.equipmentCode?? null,
                annotationView.portfolio?? null
              );
            } else {
              throw new Error(`response contains: ${JSON.stringify(response)}`);
            }
          });
        } else {
          throw new Error(`response contains: ${JSON.stringify(response)}`);
        }
      }),

  createAnnotation: (orga: string, annotation: CreationAnnotationView): Promise<Response> => {
    return httpClient.post(ApiUrlBuilder.createAnnotation(orga), { body: JSON.stringify(annotation) });
  },

  updateAnnotation: (
    orga: string,
    siteId: string | null,
    buildingId: string | null,
    floorId: string | null,
    id: string,
    annotation: UpdateAnnotationViewWithoutLocation
  ): Promise<Response> => {
    const cleanedAnnotation = { ...annotation };
    if (cleanedAnnotation.pictureBinary === "") {
      delete cleanedAnnotation.pictureBinary;
    }

    return httpClient.patch(`/api/organizations/${orga}/annotations`, {
      body: JSON.stringify({
        id,
        siteId,
        buildingId,
        floorId,
        ...cleanedAnnotation,
      } as UpdateAnnotationView),
    });
  },

  deleteAnnotation: (orga: string, annotation: DeletionAnnotationView): Promise<Response> =>
    httpClient.post(ApiUrlBuilder.deleteAnnotation(orga), { body: JSON.stringify(annotation) }),
};

export const AnnotationFloorService: Readonly<typeof AnnotationFloorServiceImplementation> =
  AnnotationFloorServiceImplementation;

const AnnotationOutdoorAreasServiceImplementation = {
  getAnnotations: (orga: string, siteId: string) => AnnotationFloorService.getAnnotations(orga, siteId, null, null),
  createAnnotation: AnnotationFloorService.createAnnotation,
  updateAnnotation: (
    orga: string,
    siteId: string,
    annotationId: string,
    annotation: UpdateAnnotationViewWithoutLocation
  ) => AnnotationFloorService.updateAnnotation(orga, siteId, siteId, siteId, annotationId, annotation),
  deleteAnnotation: (orga: string, annotationId: string, siteId: string, fileName: string | null) =>
    AnnotationFloorService.deleteAnnotation(orga, { siteId, id: annotationId, floorId: siteId, buildingId: siteId, fileName: fileName }),
};

export const AnnotationOutdoorAreasService: Readonly<typeof AnnotationOutdoorAreasServiceImplementation> =
  AnnotationOutdoorAreasServiceImplementation;

const isGetAnnotationView = (annotationView: any): annotationView is GetAnnotationView => {
  const requiredProperties = ["id", "roomId", "type", "title", "x", "y"];
  for (const prop of requiredProperties) {
    if (!(prop in annotationView)) {
      throw new Error(`Property ${prop} is missing or invalid in object: ${JSON.stringify(annotationView)}`);
    }
  }
  return true;
};

const AnnotationServiceImplementation = {
  getAllAnnotations: (orga: string, annotationFilterView: AnnotationFilterView): Promise<AnnotationModel[]> =>
    httpClient
      .post(ApiUrlBuilder.getAllAnnotations(orga),{ body : JSON.stringify(annotationFilterView) })
      .then((response) => response.json())
      .then((response) => {
        if (Array.isArray(response)) {
          return response.map((annotationView: unknown) => {
            if (isGetAnnotationView(annotationView)) {
              return new AnnotationModel(
                annotationView.id,
                annotationView.roomId,
                annotationView.type as AnnotationType,
                annotationView.title,
                annotationView.content,
                annotationView.x,
                annotationView.y,
                annotationView.pictureBinary,
                annotationView.status as AnnotationStatus,
                annotationView.fileName?? null,
                annotationView.location?? null,
                annotationView.date?? null,
                annotationView.siteId?? null,
                annotationView.siteCode?? null,
                annotationView.addedBy?? null,
                annotationView.equipmentCode?? null,
                annotationView.portfolio?? null
              );
            } else {
              throw new Error(`response contains: ${JSON.stringify(response)}`);
            }
          });
        } else {
          throw new Error(`response contains: ${JSON.stringify(response)}`);
        }
      }),

  updateAnnotationsStatus: (
    orga: string,
    updateAnnotationStatusView: UpdateAnnotationStatusView
  ): Promise<Response> =>
    httpClient.put(`/api/organizations/${orga}/annotations`, { body: JSON.stringify(updateAnnotationStatusView) }),

  updateAnnotation: (
    orga: string,
    id: string,
    updatedFields: UpdateAnnotationViewWithoutLocation
  ): Promise<Response> =>
    AnnotationFloorService.updateAnnotation(orga, null, null, null, id, updatedFields),

  deleteAnnotation: (
    orga: string,
    id: string,
    fileName: string | null
  ): Promise<Response> =>
    AnnotationFloorService.deleteAnnotation(orga, { id, siteId: id, buildingId: id, floorId: id, fileName }),

  getAnnotationById: (orga: string, annotationId: string): Promise<AnnotationModel> =>
    httpClient
      .get(`/api/organizations/${orga}/annotations/${annotationId}`)
      .then((response) => response.json())
      .then((annotationView: unknown) => {
        if (isGetAnnotationView(annotationView)) {
          return new AnnotationModel(
            annotationView.id,
            annotationView.roomId,
            annotationView.type as AnnotationType,
            annotationView.title,
            annotationView.content,
            annotationView.x,
            annotationView.y,
            annotationView.pictureBinary,
            annotationView.status as AnnotationStatus,
            annotationView.fileName ?? null,
            annotationView.location ?? null,
            annotationView.date ?? null,
            annotationView.siteId ?? null,
            annotationView.siteCode ?? null,
            annotationView.addedBy ?? null,
            annotationView.equipmentCode ?? null,
            annotationView.portfolio ?? null
          );
        } else {
          throw new Error(`response contains: ${JSON.stringify(annotationView)}`);
        }
      })
}

export const AnnotationService: Readonly<typeof AnnotationServiceImplementation> =
  AnnotationServiceImplementation;
